import {
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  GeoPoint,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  type Query as FirestoreQuery,
  QueryDocumentSnapshot,
  setDoc,
  Unsubscribe,
  updateDoc,
  writeBatch,
  where
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { RouteMarkerDraft, RouteMarkerType } from "../components/route-editor/routeMarkerTypes";
import { db, storage } from "../lib/firebase";
import { buildGpxFromRoute, safeFileName } from "../lib/routeExport";
import { enrichRoutePointsWithElevation, routePointAltitudeMeters } from "../lib/routeElevation";
import type { GeneratedRoutePoint, UserRideDetail, UserRideSummary } from "./rideService";
import { upsertEventRouteMarker } from "./routeService";

export type EventType =
  | "BREVET"
  | "RACE"
  | "GROUPRIDE"
  | "TRAINING"
  | "CHARITY"
  | "TOUR"
  | "OTHER"
  | string;

export type EventStatus = "DRAFT" | "UPCOMING" | "ONGOING" | "COMPLETED" | "CANCELLED" | "CANCELED" | string;

export type ParticipationMode = "OPEN" | "APPROVAL_REQUIRED" | "INVITE_ONLY" | string;

export type GeoPointLike = {
  latitude: number;
  longitude: number;
};

export type RoutePreviewPoint = GeoPointLike & {
  altitude?: number | null;
  timestamp?: number | null;
};

export type PulsEvent = {
  id: string;
  organizerId?: string;
  organizerName?: string;
  title: string;
  description?: string;
  eventType: EventType;
  distance?: number | null;
  targetSpeedKmh?: number | null;
  startDate?: number | null;
  endDate?: number | null;
  durationHours?: number | null;
  registrationDeadline?: number | null;
  startLocation?: GeoPointLike | null;
  startLocationName?: string;
  routePoints: RoutePreviewPoint[];
  controlPoints: RoutePreviewPoint[];
  routeFilePath?: string;
  routeFileName?: string;
  routeFileType?: string;
  photoPath?: string;
  photoUrl?: string;
  isDeleted: boolean;
  isPublic: boolean;
  maxParticipants?: number | null;
  currentParticipants?: number | null;
  participantIds: string[];
  supportUserIds: string[];
  controlUserIds: string[];
  status: EventStatus;
  requiresApproval?: boolean;
  participationMode: ParticipationMode;
  allowParticipantLiveMap?: boolean;
  createdAt?: number | null;
  updatedAt?: number | null;
};

export type BrevetResultStatus = "FINISHED" | "WITHDRAWN" | "DNF" | "DISQUALIFIED" | string;

export type BrevetResult = {
  id: string;
  eventId: string;
  athleteName: string;
  athleteNumber?: string;
  startTime?: number | null;
  finishTime?: number | null;
  durationMillis?: number | null;
  distanceMeters?: number | null;
  averageSpeedKmh?: number | null;
  maxSpeedKmh?: number | null;
  elevationGainMeters?: number | null;
  status: BrevetResultStatus;
  suspiciousFlags: string[];
  createdAt?: number | null;
  updatedAt?: number | null;
};

export type PublicEventsState = {
  events: PulsEvent[];
  loading: boolean;
  error: string | null;
};

export type EventDetailState = {
  event: PulsEvent | null;
  loading: boolean;
  error: string | null;
};

export type BrevetResultsState = {
  results: BrevetResult[];
  loading: boolean;
  error: string | null;
};

export type AttachSavedRouteOptions = {
  transferMarkers?: boolean;
};

export type AttachSavedRouteResult = {
  routeFilePath: string;
  routeFileName: string;
  routeFileType: "gpx";
  routePreviewPointCount: number;
  transferredMarkerCount: number;
};

export type ActionFormInput = {
  title: string;
  description: string;
  eventType: EventType;
  status: EventStatus;
  targetSpeedKmh: number | null;
  startDate: number;
  endDate: number | null;
  durationHours: number;
  registrationDeadline: number | null;
  startLocationName: string;
  startLatitude: number | null;
  startLongitude: number | null;
  maxParticipants: number;
  participationMode: ParticipationMode;
  allowParticipantLiveMap: boolean;
  photoUrl: string;
};

export type PublicParticipant = {
  id: string;
  eventId: string;
  userId: string;
  displayName: string;
  joinedAt?: number | null;
  updatedAt?: number | null;
};

export type InvitationSummary = {
  id: string;
  eventId: string;
  eventTitle: string;
  eventType: string;
  organizerId: string;
  organizerName: string;
  inviteeId: string;
  inviteeEmail: string;
  inviteeName: string;
  status: string;
  isExternalInvite: boolean;
  createdAt?: number | null;
  updatedAt?: number | null;
  acceptedAt?: number | null;
  declinedAt?: number | null;
};

export type SupportControlRole = "SUPPORT" | "CONTROL";

export type SupportControlRequestSummary = {
  id: string;
  eventId: string;
  eventTitle: string;
  eventEndTimeMillis: number;
  organizerId: string;
  organizerName: string;
  requestedUserId: string;
  requestedUserEmail: string;
  requestedUserName: string;
  role: SupportControlRole;
  status: string;
  createdAt?: number | null;
  updatedAt?: number | null;
  respondedAt?: number | null;
};

export type ManagementUserProfile = {
  uid: string;
  email: string;
  displayName: string;
  role: string;
  invitationCredits: number;
  invitationsSent: number;
  isAdmin: boolean;
};

export type LiveActionAccessRole = "ORGANIZER" | "SUPPORT" | "CONTROL" | "ADMIN";

export type LiveActionAccess = {
  primaryRole: LiveActionAccessRole;
  roles: LiveActionAccessRole[];
};

export type ActionInvitationInput = {
  inviteeEmail: string;
  inviteeName: string;
  inviteePhoneNumber: string;
};

export type SupportControlInput = {
  email: string;
  role: SupportControlRole;
};

const PUBLIC_EVENT_STATUSES = ["UPCOMING", "ONGOING", "COMPLETED"];
const CANCELLED_STATUSES = new Set(["CANCELLED", "CANCELED"]);
const ADMIN_UID = "MyJ1aVrIbANpcNvL4WpXA2fSLlv2";

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function booleanValue(value: unknown, fallback: boolean) {
  return typeof value === "boolean" ? value : fallback;
}

function stringList(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function millisValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();

  if (value && typeof value === "object") {
    const candidate = value as { toMillis?: () => number; seconds?: number };
    if (typeof candidate.toMillis === "function") return candidate.toMillis();
    if (typeof candidate.seconds === "number") return candidate.seconds * 1000;
  }

  return null;
}

function geoPointValue(value: unknown): GeoPointLike | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as { latitude?: unknown; longitude?: unknown };
  const latitude = numberValue(candidate.latitude);
  const longitude = numberValue(candidate.longitude);
  return latitude === null || longitude === null ? null : { latitude, longitude };
}

function startLocationValue(input: ActionFormInput) {
  if (
    typeof input.startLatitude === "number" &&
    Number.isFinite(input.startLatitude) &&
    typeof input.startLongitude === "number" &&
    Number.isFinite(input.startLongitude)
  ) {
    return new GeoPoint(input.startLatitude, input.startLongitude);
  }
  return null;
}

function effectiveInputParticipationMode(input: ActionFormInput) {
  return input.eventType === "BREVET" ? "INVITE_ONLY" : input.participationMode;
}

function commonActionWriteData(input: ActionFormInput) {
  const now = Date.now();
  return {
    title: input.title.trim(),
    description: input.description.trim(),
    eventType: input.eventType,
    targetSpeedKmh: input.targetSpeedKmh ?? 0,
    startDate: input.startDate,
    endDate: input.endDate,
    durationHours: Math.max(Math.round(input.durationHours), 1),
    registrationDeadline: input.registrationDeadline,
    startLocation: startLocationValue(input),
    startLocationName: input.startLocationName.trim(),
    maxParticipants: Math.max(Math.round(input.maxParticipants), 1),
    status: input.status,
    isPublic: true,
    requiresApproval: effectiveInputParticipationMode(input) === "APPROVAL_REQUIRED",
    participationMode: effectiveInputParticipationMode(input),
    allowParticipantLiveMap: Boolean(input.allowParticipantLiveMap),
    photoUrl: input.photoUrl.trim(),
    updatedAt: now
  };
}

function routePointList(value: unknown): RoutePreviewPoint[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item): RoutePreviewPoint | null => {
      if (!item || typeof item !== "object") return null;
      const candidate = item as Record<string, unknown>;
      const latitude = numberValue(candidate.latitude);
      const longitude = numberValue(candidate.longitude);
      if (latitude === null || longitude === null) return null;

      return {
        latitude,
        longitude,
        altitude: numberValue(candidate.altitude),
        timestamp: millisValue(candidate.timestamp)
      };
    })
    .filter((item): item is RoutePreviewPoint => item !== null);
}

function routePointWriteData(point: GeneratedRoutePoint): RoutePreviewPoint & { speed: number; accuracy?: number } {
  const altitude = routePointAltitudeMeters(point);
  return {
    latitude: point.latitude,
    longitude: point.longitude,
    timestamp: point.timestamp,
    speed: point.speed,
    ...(altitude == null ? {} : { altitude }),
    ...(typeof point.accuracy === "number" && Number.isFinite(point.accuracy) ? { accuracy: point.accuracy } : {})
  };
}

function controlPointWriteData(point: GeneratedRoutePoint) {
  return {
    ...routePointWriteData(point),
    ...(point.pointOfInterestType ? { pointOfInterestType: point.pointOfInterestType } : {}),
    ...(point.pointOfInterestName ? { pointOfInterestName: point.pointOfInterestName } : {}),
    ...(point.pointOfInterestSymbol ? { pointOfInterestSymbol: point.pointOfInterestSymbol } : {}),
    ...(point.pointOfInterestDescription ? { pointOfInterestDescription: point.pointOfInterestDescription } : {}),
    ...(point.pointOfInterestSourceType ? { pointOfInterestSourceType: point.pointOfInterestSourceType } : {})
  };
}

function simplifyEventRoutePreview(points: GeneratedRoutePoint[], maxPoints = 500) {
  if (points.length <= maxPoints) return points.map(routePointWriteData);
  if (maxPoints < 2) return points.slice(0, maxPoints).map(routePointWriteData);

  const lastIndex = points.length - 1;
  const simplified: GeneratedRoutePoint[] = [];
  let lastAddedIndex = -1;

  for (let index = 0; index < maxPoints; index += 1) {
    const sourceIndex = Math.round((index * lastIndex) / (maxPoints - 1));
    if (sourceIndex !== lastAddedIndex) {
      simplified.push(points[sourceIndex]);
      lastAddedIndex = sourceIndex;
    }
  }

  if (simplified[0] !== points[0]) simplified.unshift(points[0]);
  if (simplified[simplified.length - 1] !== points[lastIndex]) {
    if (simplified.length >= maxPoints) simplified[simplified.length - 1] = points[lastIndex];
    else simplified.push(points[lastIndex]);
  }

  return simplified.map(routePointWriteData);
}

function markerTypeForRoutePoint(point: GeneratedRoutePoint): RouteMarkerType {
  const type = (point.pointOfInterestType || point.pointOfInterestSourceType || "").toUpperCase();
  switch (type) {
    case "WATER":
      return "WATER";
    case "FOOD":
      return "FOOD";
    case "WARNING":
      return "WARNING";
    case "PHOTO":
      return "PHOTO";
    case "SERVICE":
      return "SERVICE";
    case "SLEEP":
      return "COFFEE";
    case "CONTROL":
    case "MARSHAL":
      return "CONTROL";
    default:
      return "NOTE";
  }
}

function markerKmValue(point: GeneratedRoutePoint) {
  const data = point as unknown as Record<string, unknown>;
  const km = data.km ?? data.distanceKm;
  if (typeof km === "number" && Number.isFinite(km)) return km.toFixed(2);
  const meters = data.distanceMeters ?? data.routeDistanceMeters;
  if (typeof meters === "number" && Number.isFinite(meters)) return (meters / 1000).toFixed(2);
  return "";
}

function markerTimeValue(point: GeneratedRoutePoint, keys: string[]) {
  const data = point as unknown as Record<string, unknown>;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value.trim().slice(0, 16);
  }
  return "";
}

function routeMarkerFromPoint(eventId: string, point: GeneratedRoutePoint, index: number): RouteMarkerDraft {
  return {
    id: `saved_route_${Date.now()}_${index}`,
    type: markerTypeForRoutePoint(point),
    title: (point.pointOfInterestName || point.pointOfInterestType || "Σημείο διαδρομής").slice(0, 120),
    description: (point.pointOfInterestDescription || "").slice(0, 2000),
    lat: point.latitude,
    lng: point.longitude,
    km: markerKmValue(point),
    openTime: markerTimeValue(point, ["openTime", "opensAt", "openingTime", "openAt"]),
    closeTime: markerTimeValue(point, ["closeTime", "closesAt", "closingTime", "closeAt"]),
    visibleToAthletes: true,
    order: 10000 + index + 1
  };
}

function ensureAttachableRoute(route: UserRideDetail | UserRideSummary) {
  if (route.routePoints.length < 2) throw new Error("route-points-required");
}

function normalizeStatus(status: unknown): EventStatus {
  return stringValue(status, "UPCOMING").toUpperCase();
}

function normalizeEventType(eventType: unknown): EventType {
  return stringValue(eventType, "OTHER").toUpperCase();
}

function normalizeParticipationMode(participationMode: unknown): ParticipationMode {
  return stringValue(participationMode, "OPEN").toUpperCase();
}

function eventFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>): PulsEvent {
  const data = snapshot.data() ?? {};
  const eventType = normalizeEventType(data.eventType);

  return {
    id: stringValue(data.id, snapshot.id),
    organizerId: stringValue(data.organizerId),
    organizerName: stringValue(data.organizerName),
    title: stringValue(data.title, "Άτιτλη δράση"),
    description: stringValue(data.description),
    eventType,
    distance: numberValue(data.distance),
    targetSpeedKmh: numberValue(data.targetSpeedKmh),
    startDate: millisValue(data.startDate),
    endDate: millisValue(data.endDate),
    durationHours: numberValue(data.durationHours),
    registrationDeadline: millisValue(data.registrationDeadline),
    startLocation: geoPointValue(data.startLocation),
    startLocationName: stringValue(data.startLocationName),
    routePoints: routePointList(data.routePoints),
    controlPoints: routePointList(data.controlPoints),
    routeFilePath: stringValue(data.routeFilePath),
    routeFileName: stringValue(data.routeFileName),
    routeFileType: stringValue(data.routeFileType),
    photoPath: stringValue(data.photoPath),
    photoUrl: stringValue(data.photoUrl),
    isDeleted: booleanValue(data.isDeleted, false),
    isPublic: booleanValue(data.isPublic, true),
    maxParticipants: numberValue(data.maxParticipants),
    currentParticipants: numberValue(data.currentParticipants),
    participantIds: stringList(data.participantIds),
    supportUserIds: stringList(data.supportUserIds),
    controlUserIds: stringList(data.controlUserIds),
    status: normalizeStatus(data.status),
    requiresApproval: booleanValue(data.requiresApproval, false),
    participationMode: normalizeParticipationMode(data.participationMode),
    allowParticipantLiveMap: booleanValue(data.allowParticipantLiveMap, false),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt)
  };
}

function publicParticipantFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): PublicParticipant {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    eventId: stringValue(data.eventId),
    userId: stringValue(data.userId, snapshot.id),
    displayName: stringValue(data.displayName, "Συμμετέχων"),
    joinedAt: millisValue(data.joinedAt),
    updatedAt: millisValue(data.updatedAt)
  };
}

function invitationFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): InvitationSummary {
  const data = snapshot.data();
  return {
    id: stringValue(data.id, snapshot.id),
    eventId: stringValue(data.eventId),
    eventTitle: stringValue(data.eventTitle),
    eventType: stringValue(data.eventType),
    organizerId: stringValue(data.organizerId),
    organizerName: stringValue(data.organizerName),
    inviteeId: stringValue(data.inviteeId),
    inviteeEmail: stringValue(data.inviteeEmail),
    inviteeName: stringValue(data.inviteeName),
    status: stringValue(data.status, "PENDING").toUpperCase(),
    isExternalInvite: booleanValue(data.isExternalInvite, false),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt),
    acceptedAt: millisValue(data.acceptedAt),
    declinedAt: millisValue(data.declinedAt)
  };
}

function supportControlRequestFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): SupportControlRequestSummary {
  const data = snapshot.data();
  const role = stringValue(data.role, "SUPPORT").toUpperCase() === "CONTROL" ? "CONTROL" : "SUPPORT";
  return {
    id: stringValue(data.id, snapshot.id),
    eventId: stringValue(data.eventId),
    eventTitle: stringValue(data.eventTitle),
    eventEndTimeMillis: numberValue(data.eventEndTimeMillis) ?? 0,
    organizerId: stringValue(data.organizerId),
    organizerName: stringValue(data.organizerName),
    requestedUserId: stringValue(data.requestedUserId),
    requestedUserEmail: stringValue(data.requestedUserEmail),
    requestedUserName: stringValue(data.requestedUserName),
    role,
    status: stringValue(data.status, "PENDING").toUpperCase(),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt),
    respondedAt: millisValue(data.respondedAt)
  };
}

function managementProfileFromSnapshot(snapshot: DocumentSnapshot<DocumentData>, authUser: { uid: string; email?: string | null; displayName?: string | null }): ManagementUserProfile {
  const data = snapshot.data() ?? {};
  const role = stringValue(data.role, "");
  return {
    uid: authUser.uid,
    email: stringValue(data.email, authUser.email ?? ""),
    displayName: stringValue(data.displayName, authUser.displayName ?? ""),
    role,
    invitationCredits: numberValue(data.invitationCredits) ?? 0,
    invitationsSent: numberValue(data.invitationsSent) ?? 0,
    isAdmin: authUser.uid === ADMIN_UID || role === "admin"
  };
}

function eventEffectiveEndTimeMillis(event: PulsEvent) {
  if (event.endDate) return event.endDate;
  if (event.startDate) return event.startDate + Math.max(event.durationHours ?? 1, 1) * 60 * 60 * 1000;
  return 0;
}

function cleanEmail(value: string) {
  return value.trim().toLowerCase();
}

function brevetResultFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): BrevetResult {
  const data = snapshot.data();

  return {
    id: stringValue(data.id, snapshot.id),
    eventId: stringValue(data.eventId),
    athleteName: stringValue(data.athleteName, "Αθλητής"),
    athleteNumber: stringValue(data.athleteNumber),
    startTime: millisValue(data.startTime),
    finishTime: millisValue(data.finishTime),
    durationMillis: numberValue(data.durationMillis),
    distanceMeters: numberValue(data.distanceMeters),
    averageSpeedKmh: numberValue(data.averageSpeedKmh),
    maxSpeedKmh: numberValue(data.maxSpeedKmh),
    elevationGainMeters: numberValue(data.elevationGainMeters),
    status: stringValue(data.status, "FINISHED").toUpperCase(),
    suspiciousFlags: stringList(data.suspiciousFlags),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt)
  };
}

export function effectiveParticipationMode(event: PulsEvent): ParticipationMode {
  return event.eventType === "BREVET" ? "INVITE_ONLY" : event.participationMode;
}

export function getEffectiveEventStatus(event: PulsEvent, now = Date.now()): EventStatus {
  const stored = normalizeStatus(event.status);
  if (stored === "DRAFT" || CANCELLED_STATUSES.has(stored)) return stored;
  if (!event.startDate) return stored;

  const durationHours = Math.max(event.durationHours ?? 1, 1);
  const effectiveEndDate = event.endDate ?? event.startDate + durationHours * 60 * 60 * 1000;

  if (now < event.startDate) return "UPCOMING";
  if (now <= effectiveEndDate) return "ONGOING";
  return "COMPLETED";
}

export function isPublicEventVisible(event: PulsEvent) {
  const status = normalizeStatus(event.status);
  return event.isPublic && !event.isDeleted && status !== "DRAFT" && !CANCELLED_STATUSES.has(status);
}

export function eventTypeLabel(eventType: EventType) {
  switch (eventType) {
    case "BREVET":
      return "Brevet";
    case "RACE":
      return "Αγώνας";
    case "GROUPRIDE":
      return "Βόλτα";
    case "TRAINING":
      return "Προπόνηση";
    case "CHARITY":
      return "Φιλανθρωπική";
    case "TOUR":
      return "Περιήγηση";
    default:
      return "Άλλη δράση";
  }
}

export function eventStatusLabel(status: EventStatus) {
  switch (normalizeStatus(status)) {
    case "UPCOMING":
      return "Προσεχώς";
    case "ONGOING":
      return "Σε εξέλιξη";
    case "COMPLETED":
      return "Ολοκληρώθηκε";
    case "DRAFT":
      return "Πρόχειρο";
    case "CANCELLED":
    case "CANCELED":
      return "Ακυρώθηκε";
    default:
      return stringValue(status, "Κατάσταση");
  }
}

export function getMyLiveActionAccess(
  event: PulsEvent,
  userId: string | null | undefined,
  isAdmin = false
): LiveActionAccess | null {
  const normalizedUserId = (userId ?? "").trim();
  if (!normalizedUserId && !isAdmin) return null;
  if (event.isDeleted || getEffectiveEventStatus(event) !== "ONGOING") return null;

  const roles: LiveActionAccessRole[] = [];
  if (normalizedUserId && event.organizerId === normalizedUserId) roles.push("ORGANIZER");
  if (normalizedUserId && event.supportUserIds.includes(normalizedUserId)) roles.push("SUPPORT");
  if (normalizedUserId && event.controlUserIds.includes(normalizedUserId)) roles.push("CONTROL");
  if (isAdmin) roles.push("ADMIN");

  return roles.length > 0 ? { primaryRole: roles[0], roles } : null;
}

export function participationModeLabel(mode: ParticipationMode) {
  switch (mode) {
    case "OPEN":
      return "Ανοιχτή συμμετοχή";
    case "APPROVAL_REQUIRED":
      return "Με έγκριση";
    case "INVITE_ONLY":
      return "Με πρόσκληση";
    default:
      return stringValue(mode, "Συμμετοχή");
  }
}

export function formatEventDistance(event: PulsEvent) {
  if (event.distance && event.distance > 0) return `${event.distance.toFixed(1)} km`;
  if (event.routePoints.length > 0) return `${event.routePoints.length} σημεία`;
  return "Δεν έχει δηλωθεί";
}

export function eventSearchValues(event: PulsEvent) {
  return [
    event.title,
    event.description,
    event.startLocationName,
    event.organizerName,
    eventTypeLabel(event.eventType),
    participationModeLabel(effectiveParticipationMode(event)),
    event.routeFileName
  ];
}

export function sortEventsForDisplay(events: PulsEvent[]) {
  return [...events].sort((a, b) => {
    const aStatus = getEffectiveEventStatus(a);
    const bStatus = getEffectiveEventStatus(b);
    const statusWeight = (status: EventStatus) => {
      if (status === "ONGOING") return 0;
      if (status === "UPCOMING") return 1;
      return 2;
    };
    const statusDiff = statusWeight(aStatus) - statusWeight(bStatus);
    if (statusDiff !== 0) return statusDiff;
    return (a.startDate ?? Number.MAX_SAFE_INTEGER) - (b.startDate ?? Number.MAX_SAFE_INTEGER);
  });
}

export async function listEditableActionsForUser(userId: string) {
  if (!userId.trim()) return [];

  const snapshot = await getDocs(query(collection(db, "events"), where("organizerId", "==", userId)));
  return sortEventsForDisplay(snapshot.docs.map(eventFromSnapshot).filter((event) => !event.isDeleted));
}

export async function createAction(input: ActionFormInput, organizer: { uid: string; displayName?: string | null }) {
  const eventRef = doc(collection(db, "events"));
  const now = Date.now();

  const data = {
    ...commonActionWriteData(input),
    id: eventRef.id,
    organizerId: organizer.uid,
    organizerName: organizer.displayName || "PULS organizer",
    organizerEmail: "",
    distance: 0,
    startLocation: startLocationValue(input),
    routePoints: [],
    controlPoints: [],
    routeFilePath: "",
    routeFileName: "",
    routeFileType: "",
    photoPath: "",
    isDeleted: false,
    maxParticipants: Math.max(Math.round(input.maxParticipants), 1),
    currentParticipants: 0,
    participantIds: [],
    waitlistIds: [],
    supportUserIds: [],
    controlUserIds: [],
    createdAt: now,
    updatedAt: now,
    viewCount: 0,
    shareCount: 0
  };

  await setDoc(eventRef, data);
  return eventRef.id;
}

export async function updateAction(eventId: string, input: ActionFormInput) {
  await updateDoc(doc(db, "events", eventId), commonActionWriteData(input));
}

export async function getManagementUserProfile(authUser: { uid: string; email?: string | null; displayName?: string | null }) {
  const snapshot = await getDoc(doc(db, "users", authUser.uid));
  return managementProfileFromSnapshot(snapshot, authUser);
}

export function subscribePublicParticipants(
  eventId: string,
  callback: (state: { participants: PublicParticipant[]; loading: boolean; error: string | null }) => void
): Unsubscribe {
  callback({ participants: [], loading: true, error: null });

  return onSnapshot(
    collection(db, "events", eventId, "publicParticipants"),
    (snapshot) => {
      const participants = snapshot.docs
        .map(publicParticipantFromSnapshot)
        .sort((a, b) => a.displayName.localeCompare(b.displayName, "el"));
      callback({ participants, loading: false, error: null });
    },
    (error) => {
      callback({ participants: [], loading: false, error: error.message });
    }
  );
}

export function subscribeActionInvitations(
  eventId: string,
  organizerId: string,
  callback: (state: { invitations: InvitationSummary[]; loading: boolean; error: string | null }) => void
): Unsubscribe {
  callback({ invitations: [], loading: true, error: null });

  const invitationsQuery = query(
    collection(db, "invitations"),
    where("eventId", "==", eventId),
    where("organizerId", "==", organizerId)
  );

  return onSnapshot(
    invitationsQuery,
    (snapshot) => {
      const invitations = snapshot.docs
        .map(invitationFromSnapshot)
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      callback({ invitations, loading: false, error: null });
    },
    (error) => {
      callback({ invitations: [], loading: false, error: error.message });
    }
  );
}

export function subscribeEventSupportControlRequests(
  eventId: string,
  callback: (state: { requests: SupportControlRequestSummary[]; loading: boolean; error: string | null }) => void
): Unsubscribe {
  callback({ requests: [], loading: true, error: null });

  return onSnapshot(
    collection(db, "events", eventId, "supportControlRequests"),
    (snapshot) => {
      const requests = snapshot.docs
        .map(supportControlRequestFromSnapshot)
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      callback({ requests, loading: false, error: null });
    },
    (error) => {
      callback({ requests: [], loading: false, error: error.message });
    }
  );
}

export async function createExternalActionInvitation(
  event: PulsEvent,
  organizer: ManagementUserProfile,
  input: ActionInvitationInput
) {
  const inviteeEmail = cleanEmail(input.inviteeEmail);
  const inviteePhoneNumber = input.inviteePhoneNumber.trim();
  const inviteeName = input.inviteeName.trim();

  if (!inviteeEmail) throw new Error("email-required");
  if (!event.id || !event.organizerId) throw new Error("event-required");
  if (!organizer.isAdmin && organizer.invitationCredits <= 0) throw new Error("no-invitation-credits");

  const now = Date.now();
  const invitationRef = doc(collection(db, "invitations"));
  const batch = writeBatch(db);
  batch.set(invitationRef, {
    id: invitationRef.id,
    eventId: event.id,
    eventTitle: event.title,
    eventType: event.eventType,
    organizerId: organizer.uid,
    organizerName: organizer.displayName || organizer.email || event.organizerName || "",
    inviteeId: "",
    inviteeEmail,
    inviteePhoneNumber,
    inviteeName,
    isExternalInvite: true,
    claimedAt: null,
    status: "PENDING",
    isRead: false,
    createdAt: now,
    updatedAt: now,
    acceptedAt: null,
    declinedAt: null
  });

  batch.update(doc(db, "users", organizer.uid), {
    invitationCredits: organizer.isAdmin ? organizer.invitationCredits : Math.max(organizer.invitationCredits - 1, 0),
    invitationsSent: organizer.invitationsSent + 1,
    updatedAt: now
  });

  await batch.commit();
  return invitationRef.id;
}

async function findUserByEmail(email: string) {
  const snapshot = await getDocs(query(collection(db, "users"), where("email", "==", cleanEmail(email))));
  const document = snapshot.docs[0];
  if (!document) return null;
  const data = document.data();
  return {
    uid: stringValue(data.uid, document.id),
    email: stringValue(data.email),
    displayName: stringValue(data.displayName)
  };
}

export async function createSupportControlRequestByEmail(
  event: PulsEvent,
  organizer: ManagementUserProfile,
  input: SupportControlInput
) {
  const email = cleanEmail(input.email);
  if (!email) throw new Error("email-required");
  if (!event.id || !event.organizerId) throw new Error("event-required");

  const targetUser = await findUserByEmail(email);
  if (!targetUser) throw new Error("user-not-found");
  if (targetUser.uid === organizer.uid) throw new Error("cannot-request-self");
  if (input.role === "SUPPORT" && event.supportUserIds.includes(targetUser.uid)) {
    throw new Error("already-support");
  }
  if (input.role === "CONTROL" && event.controlUserIds.includes(targetUser.uid)) {
    throw new Error("already-control");
  }

  const existingRequests = await getDocs(collection(db, "events", event.id, "supportControlRequests"));
  const duplicate = existingRequests.docs
    .map(supportControlRequestFromSnapshot)
    .some((request) => request.requestedUserId === targetUser.uid && request.role === input.role && request.status === "PENDING");
  if (duplicate) throw new Error("duplicate-support-control-request");

  const now = Date.now();
  const requestRef = doc(collection(db, "supportControlRequests"));
  const data = {
    id: requestRef.id,
    eventId: event.id,
    eventTitle: event.title,
    eventEndTimeMillis: eventEffectiveEndTimeMillis(event),
    organizerId: event.organizerId,
    organizerName: organizer.displayName || organizer.email || event.organizerName || "",
    requestedUserId: targetUser.uid,
    requestedUserEmail: targetUser.email,
    requestedUserName: targetUser.displayName,
    role: input.role,
    status: "PENDING",
    createdAt: now,
    updatedAt: now,
    respondedAt: null
  };

  const batch = writeBatch(db);
  batch.set(requestRef, data);
  batch.set(doc(db, "events", event.id, "supportControlRequests", requestRef.id), data);
  await batch.commit();
  return requestRef.id;
}

export async function attachSavedRouteToAction(
  eventId: string,
  route: UserRideDetail | UserRideSummary,
  options: AttachSavedRouteOptions = {}
): Promise<AttachSavedRouteResult> {
  ensureAttachableRoute(route);
  const elevationResult = await enrichRoutePointsWithElevation(route.routePoints);
  const routeWithElevation = {
    ...route,
    routePoints: elevationResult.points
  };

  const routeFileName = safeFileName(routeWithElevation.title, "puls-route.gpx");
  const routeFileType = "gpx" as const;
  const routeFilePath = `event_routes/${eventId}/${Date.now()}_${routeFileName}`;
  const gpx = buildGpxFromRoute(routeWithElevation);
  const routeRef = ref(storage, routeFilePath);

  await uploadBytes(routeRef, new Blob([gpx], { type: "application/gpx+xml" }), {
    contentType: "application/gpx+xml",
    customMetadata: {
      eventId,
      routeFileName,
      routeFileType
    }
  });

  const routePreviewPoints = simplifyEventRoutePreview(routeWithElevation.routePoints);
  const controlPoints = routeWithElevation.controlPoints.map(controlPointWriteData);

  await updateDoc(doc(db, "events", eventId), {
    routeFilePath,
    routeFileName,
    routeFileType,
    routePoints: routePreviewPoints,
    controlPoints,
    distance: route.distanceMeters > 0 ? route.distanceMeters / 1000 : 0,
    updatedAt: Date.now()
  });

  let transferredMarkerCount = 0;
  if (options.transferMarkers && routeWithElevation.controlPoints.length > 0) {
    for (const [index, point] of routeWithElevation.controlPoints.entries()) {
      await upsertEventRouteMarker(eventId, routeMarkerFromPoint(eventId, point, index));
      transferredMarkerCount += 1;
    }
  }

  return {
    routeFilePath,
    routeFileName,
    routeFileType,
    routePreviewPointCount: routePreviewPoints.length,
    transferredMarkerCount
  };
}

export function splitBrevetResults(results: BrevetResult[]) {
  const finished = results
    .filter((result) => result.status === "FINISHED")
    .sort((a, b) => {
      const durationDiff = (a.durationMillis ?? Number.MAX_SAFE_INTEGER) - (b.durationMillis ?? Number.MAX_SAFE_INTEGER);
      if (durationDiff !== 0) return durationDiff;
      return (a.finishTime ?? Number.MAX_SAFE_INTEGER) - (b.finishTime ?? Number.MAX_SAFE_INTEGER);
    });

  const nonFinished = results
    .filter((result) => result.status !== "FINISHED")
    .sort((a, b) => {
      const statusOrder = (status: BrevetResultStatus) => {
        if (status === "WITHDRAWN") return 0;
        if (status === "DNF") return 1;
        if (status === "DISQUALIFIED") return 2;
        return 3;
      };
      const statusDiff = statusOrder(a.status) - statusOrder(b.status);
      if (statusDiff !== 0) return statusDiff;
      return (a.finishTime ?? Number.MAX_SAFE_INTEGER) - (b.finishTime ?? Number.MAX_SAFE_INTEGER);
    });

  return { finished, nonFinished };
}

export function subscribePublicEvents(callback: (state: PublicEventsState) => void): Unsubscribe {
  callback({ events: [], loading: true, error: null });

  const publicEventsQuery = query(
    collection(db, "events"),
    where("isPublic", "==", true),
    where("isDeleted", "==", false),
    where("status", "in", PUBLIC_EVENT_STATUSES)
  );

  return onSnapshot(
    publicEventsQuery,
    (snapshot) => {
      const events = sortEventsForDisplay(snapshot.docs.map(eventFromSnapshot).filter(isPublicEventVisible));
      callback({ events, loading: false, error: null });
    },
    (error) => {
      callback({ events: [], loading: false, error: error.message });
    }
  );
}

export function subscribeUserEvents(
  userId: string,
  callback: (state: PublicEventsState) => void,
  options: { includeAdminEvents?: boolean } = {}
): Unsubscribe {
  callback({ events: [], loading: true, error: null });

  if (!userId.trim()) {
    callback({ events: [], loading: false, error: null });
    return () => {};
  }

  const eventsBySource = new Map<string, PulsEvent[]>();
  const loadedSources = new Set<string>();
  const errors = new Map<string, string>();
  const sourceEntries: Array<[string, FirestoreQuery<DocumentData>]> = [
    ["organizer", query(collection(db, "events"), where("organizerId", "==", userId))],
    ["participant", query(collection(db, "events"), where("participantIds", "array-contains", userId))],
    ["support", query(collection(db, "events"), where("supportUserIds", "array-contains", userId))],
    ["control", query(collection(db, "events"), where("controlUserIds", "array-contains", userId))]
  ];

  if (options.includeAdminEvents) {
    sourceEntries.push(["admin", query(collection(db, "events"))]);
  }

  const sourceCount = sourceEntries.length;

  function emitMergedEvents() {
    const events = Array.from(eventsBySource.values())
      .flat()
      .reduce<Map<string, PulsEvent>>((merged, event) => {
        if (!event.isDeleted) merged.set(event.id, event);
        return merged;
      }, new Map());

    const sortedEvents = Array.from(events.values()).sort((a, b) => {
      const createdDiff = (b.createdAt ?? 0) - (a.createdAt ?? 0);
      if (createdDiff !== 0) return createdDiff;
      return (a.startDate ?? Number.MAX_SAFE_INTEGER) - (b.startDate ?? Number.MAX_SAFE_INTEGER);
    });

    callback({
      events: sortedEvents,
      loading: loadedSources.size < sourceCount,
      error: errors.size > 0 ? Array.from(errors.values()).join(" · ") : null
    });
  }

  function listenToSource(sourceKey: string, sourceQuery: FirestoreQuery<DocumentData>) {
    return onSnapshot(
      sourceQuery,
      (snapshot) => {
        eventsBySource.set(sourceKey, snapshot.docs.map(eventFromSnapshot));
        loadedSources.add(sourceKey);
        errors.delete(sourceKey);
        emitMergedEvents();
      },
      (error) => {
        eventsBySource.set(sourceKey, []);
        loadedSources.add(sourceKey);
        errors.set(sourceKey, error.message);
        emitMergedEvents();
      }
    );
  }

  const unsubscribes = sourceEntries.map(([sourceKey, sourceQuery]) => listenToSource(sourceKey, sourceQuery));

  return () => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
}

export async function getEventById(eventId: string) {
  const snapshot = await getDoc(doc(db, "events", eventId));
  return snapshot.exists() ? eventFromSnapshot(snapshot) : null;
}

export function subscribeEventById(eventId: string, callback: (state: EventDetailState) => void): Unsubscribe {
  callback({ event: null, loading: true, error: null });

  return onSnapshot(
    doc(db, "events", eventId),
    (snapshot) => {
      callback({
        event: snapshot.exists() ? eventFromSnapshot(snapshot) : null,
        loading: false,
        error: null
      });
    },
    (error) => {
      callback({ event: null, loading: false, error: error.message });
    }
  );
}

export function subscribeBrevetResultsForEvent(
  eventId: string,
  callback: (state: BrevetResultsState) => void
): Unsubscribe {
  callback({ results: [], loading: true, error: null });

  const resultsQuery = query(collection(db, "brevetResults"), where("eventId", "==", eventId));

  return onSnapshot(
    resultsQuery,
    (snapshot) => {
      callback({
        results: snapshot.docs.map(brevetResultFromSnapshot),
        loading: false,
        error: null
      });
    },
    (error) => {
      callback({ results: [], loading: false, error: error.message });
    }
  );
}
